import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
    HIGHLIGHT_ANIMATION_DELAY,
    HIGHLIGHT_ANIMATION_REMOVE_DELAY,
} from "app/pages/sales-intelligence/constants/highlight-animation";
import type { SignalId } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { setSignalIdForHighlight } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { selectSignalIdForHighlight } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

const scrollToSignal = (signalId: SignalId): void => {
    const signalElement = document.querySelector<HTMLElement>(`[data-automation-signal-id="${signalId}"]`);

    signalElement?.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const useHighlightCreatedSignal = (isLoading: boolean, signalIds: readonly SignalId[]): SignalId | null => {
    const dispatch = useDispatch();
    const signalIdForHighlight = useSelector(selectSignalIdForHighlight);
    const [animatingSignalId, setAnimatingSignalId] = useState<SignalId | null>(null);
    const [highlightedSignalId, setHighlightedSignalId] = useState<SignalId | null>(null);

    const readySignalId =
        !isLoading && signalIdForHighlight && signalIds.includes(signalIdForHighlight) ? signalIdForHighlight : null;

    if (readySignalId && readySignalId !== animatingSignalId) {
        setAnimatingSignalId(readySignalId);
    }

    useEffect(() => {
        if (!animatingSignalId) {
            return () => {
                dispatch(setSignalIdForHighlight(null));
            };
        }

        dispatch(setSignalIdForHighlight(null));

        const timeoutIds: number[] = [];

        const scheduleTimeout = (callback: () => void, delay: number): void => {
            timeoutIds.push(window.setTimeout(callback, delay));
        };

        scheduleTimeout(() => {
            scrollToSignal(animatingSignalId);
            setHighlightedSignalId(animatingSignalId);

            scheduleTimeout(() => {
                setHighlightedSignalId(null);
                setAnimatingSignalId(null);
            }, HIGHLIGHT_ANIMATION_REMOVE_DELAY);
        }, HIGHLIGHT_ANIMATION_DELAY);

        return () => {
            timeoutIds.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            setHighlightedSignalId(null);
        };
    }, [animatingSignalId]);

    return highlightedSignalId;
};
